/**
 * หน้าจอ Packing (P2-9f) — เปิดกล่องต่อบรรทัดใบสั่งขาย ใส่ lot ยืนยันปิดกล่อง และ
 * เปิดกล่องที่ปิดเร็วไปกลับมาแก้ (D-105)
 *
 * โครงยืมมาจาก `SalesOrderScreen` โดยตั้งใจ (invariant ข้อสองของ
 * `WO-P2-M9-006`): ช่องค้นหา/ตัวกรองบนสุด ฟอร์มสร้างอยู่หลังปุ่มในกล่อง `Modal`
 * ปุ่ม lifecycle รายแถว และหลังสำเร็จก็โหลดรายการใหม่ผ่าน client เส้นเดิม
 *
 * ⚠️ **`docs/API_CONTRACT.md` เอ่ยถึง `POST /v1/packing-units/{id}/lots` แต่
 * router จริงไม่ได้ลงทะเบียน route นั้น** — ดูหมายเหตุยาวใน `lib/packing/types.ts`
 * lot ทั้งหมดของกล่องจึงต้องกรอกในฟอร์มสร้างกล่องเดียวกันกับหัวกล่อง ไม่มีปุ่ม
 * "เพิ่ม lot" แยกต่างหากสำหรับกล่องที่เปิดอยู่แล้ว
 *
 * ความต่างจาก `SalesOrderScreen` สามข้อที่ invariant ของ Work Order นี้เขียนไว้:
 *
 * 1. **ผลรวม lot คือหัวใจของฟอร์มสร้าง** — ไม่มี constraint ฐานข้อมูลถือกฎ
 *    "ผลรวม lot ต้องเท่าจำนวนกล่อง" (D-105 ข้อ 1) ฟอร์มจึงต้องโชว์ผลรวมและผลต่าง
 *    สดขณะกรอก (`packingSumSummary`) และปุ่มยืนยันต้อง**ไม่ถูกปิดเมื่อไม่ตรง** —
 *    กฎนี้ต้องยังเดินทางไปให้เซิร์ฟเวอร์ปฏิเสธจริงเสมอ ไม่ใช่ถูกดักไว้ที่นี่
 * 2. **ปุ่มที่ขาดสิทธิ์ยังคงแสดง แต่กดไม่ได้พร้อมเหตุผล** ต่างจาก
 *    `SalesOrderScreen` ที่ซ่อนปุ่มไปเลย — invariant ข้อ "PERMISSIONS ARE
 *    ENFORCED AT THE CONTROL, NOT AT THE DOOR" ของ Work Order นี้ระบุไว้ตรง ๆ
 * 3. **unpack ใช้ EDIT เหมือน confirm** ไม่ใช่ APPROVE — APPROVE สงวนไว้ให้การ
 *    กลับ Delivery ซึ่งไม่ได้อยู่บนหน้าจอนี้ (`WO-P2-M9-007`)
 *
 * การ gate สิทธิ์: `SALES` + `VIEW` เป็นสิทธิ์ของ**การเปิดหน้าจอนี้**
 * (ด่านอยู่ที่ route) ส่วน `CREATE` (เปิดกล่องใหม่) และ `EDIT` (ยืนยัน/เปิดกล่อง
 * ใหม่) เป็นสิทธิ์ของ**ปุ่มที่เขียน** ซึ่งถูกบังคับข้างในนี้ทีละปุ่ม ตรงกับด่านจริง
 * ฝั่งเซิร์ฟเวอร์ทีละเส้น
 *
 * ⚠️ ทั้งหมดนี้เป็น UX layer ไม่ใช่ด่านความปลอดภัย เซิร์ฟเวอร์เป็นผู้ตัดสินทุก
 * request เสมอ (`references/authorization-rbac.md` ข้อ 5)
 */

import { useCallback, useEffect, useState } from 'react';

import { StatusBadge } from '../StatusBadge';
import { InfoHint } from '../components/InfoHint';
import { Modal } from '../components/Modal';
import { can } from '../../lib/permissions/client';
import type { PermissionsState } from '../../lib/permissions/types';
import {
  confirmPackingUnit,
  createPackingUnit,
  fetchPackingUnits,
  ruleFromFailure,
  unpackPackingUnit,
  type ApiErrorEnvelope,
  type PackingUnitFailure,
  type PackingUnitTransition,
} from '../../lib/packing/client';
import {
  EMPTY_CREATE_PACKING_UNIT_FIELDS,
  EMPTY_PACKING_UNIT_LOT_DRAFT,
  PACKING_UNIT_STATUS_DESCRIPTORS,
  packingSumSummary,
  packingUnitActionLabel,
  packingUnitActionPermission,
  packingUnitLifecycleActionsFor,
  packingUnitStatusLabel,
  packingUnitStatusTone,
  toCreatePackingUnitBody,
  toPackingUnitSoNumberFilter,
  toPackingUnitStatusFilter,
  type CreatePackingUnitFields,
  type PackingUnitLotDraft,
} from '../../lib/packing/packingView';
import {
  INSUFFICIENT_STOCK_AT_SHIPPING_LOCATION,
  LOT_DUPLICATED_IN_PACKAGE,
  LOT_NOT_FOUND,
  LOT_NOT_RELEASED,
  LOT_PLANT_MUST_MATCH_PACKAGE,
  LOT_PRODUCT_MUST_MATCH_PACKAGE,
  PACKAGE_NO_TAKEN,
  PACKED_QUANTITY_MUST_MATCH_LOT_TOTAL,
  PACKING_MODULE,
  PACKING_STANDARD_NOT_ACTIVE,
  PACKING_UNIT_CANNOT_BE_UNPACKED,
  PACKING_UNIT_FIELD_IS_SERVER_OWNED,
  PACKING_UNIT_HAS_NO_LOTS,
  PACKING_UNIT_STATUS_CHANGED,
  PACKING_UNIT_TRANSITION_NOT_ALLOWED,
  PLANT_CODE_MAX_LENGTH,
  PLANT_OUTSIDE_SCOPE,
  PRODUCT_CODE_MAX_LENGTH,
  PRODUCT_MUST_MATCH_SALES_ORDER_LINE,
  PRODUCT_NOT_FOUND,
  QUANTITY_MUST_BE_POSITIVE,
  RETURNED_PACKAGE_CANNOT_BE_UNPACKED,
  SALES_ORDER_LINE_NOT_FOUND,
  SALES_ORDER_NOT_FOUND,
  SALES_ORDER_NOT_PACKABLE,
  SO_NUMBER_MAX_LENGTH,
  STORAGE_LOCATION_CODE_MAX_LENGTH,
  STORAGE_LOCATION_NOT_FOUND,
  type PackingUnit,
  type PackingUnitSummary,
} from '../../lib/packing/types';

/* ------------------------------------------------- หน้าจอความล้มเหลว ----- */

type PackingUnitFailureContext = 'list' | 'create' | PackingUnitTransition;

interface FailureCopy {
  readonly heading: string;
  readonly text: string;
}

function nothingChangedText(context: PackingUnitFailureContext): string {
  switch (context) {
    case 'create':
      return ' ไม่มีกล่องใดถูกเปิด ไม่มี lot ใดถูกเขียน และไม่มีแถว audit ถูกเขียน — เปิดกล่องเป็นธุรกรรมเดียว';
    case 'confirm':
    case 'unpack':
      return ' กล่องนี้ยังอยู่ในสถานะเดิมและไม่มีแถว audit ถูกเขียน';
    case 'list':
      return '';
  }
}

function isWriteContext(context: PackingUnitFailureContext): boolean {
  return nothingChangedText(context) !== '';
}

/**
 * ข้อความของกฎแต่ละข้อ — คืน `null` เมื่อไม่ใช่กฎที่ router นี้ตอบจริง
 *
 * ⚠️ ตัวเลขที่แม่นยำ (lot, ผลต่าง, จำนวนคงเหลือ ฯลฯ) มาจาก `details` ที่แสดงเป็น
 * รายการแยกด้านล่างข้อความนี้เสมอ — ข้อความตรงนี้อธิบาย**กฎ** ส่วนตัวเลขจริงมา
 * จากเซิร์ฟเวอร์โดยตรง ไม่ใช่ค่าที่หน้าจอคำนวณเอง
 */
function ruleCopy(rule: string | null): FailureCopy | null {
  switch (rule) {
    case PLANT_OUTSIDE_SCOPE:
      return {
        heading: 'โรงงานที่ระบุอยู่นอกขอบเขตของคุณ',
        text: `เซิร์ฟเวอร์ตอบ 403 พร้อมกฎ ${PLANT_OUTSIDE_SCOPE} — การเปิด/ยืนยัน/เปิดกล่องใหม่เป็นการเขียนเข้าโรงงานหนึ่ง (D-004) ตรวจรหัสโรงงานอีกครั้ง`,
      };
    case SALES_ORDER_NOT_FOUND:
      return {
        heading: 'ไม่พบใบสั่งขายเลขนี้ในโรงงานนี้',
        text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${SALES_ORDER_NOT_FOUND} — ไม่มีใบสั่งขายเลขนี้ในโรงงานที่ระบุ (ใบที่อยู่โรงงานอื่นตอบเหมือนกันโดยตั้งใจ)`,
      };
    case SALES_ORDER_NOT_PACKABLE:
      return {
        heading: 'ใบสั่งขายนี้อยู่ในสถานะที่แพ็กไม่ได้',
        text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${SALES_ORDER_NOT_PACKABLE} — ต้องเป็นใบที่ยืนยัน/วางแผน/ส่งบางส่วนแล้วเท่านั้น สถานะจริงของใบอยู่ในรายละเอียดด้านล่าง`,
      };
    case SALES_ORDER_LINE_NOT_FOUND:
      return {
        heading: 'ใบสั่งขายนี้ไม่มีเลขบรรทัดที่ระบุ',
        text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${SALES_ORDER_LINE_NOT_FOUND} — ตรวจเลขบรรทัดจากหน้าใบสั่งขายอีกครั้ง`,
      };
    case PRODUCT_NOT_FOUND:
      return {
        heading: 'ไม่พบสินค้ารหัสนี้',
        text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${PRODUCT_NOT_FOUND} — ไม่มีสินค้ารหัสนี้ในทะเบียน`,
      };
    case PRODUCT_MUST_MATCH_SALES_ORDER_LINE:
      return {
        heading: 'สินค้าที่ระบุไม่ตรงกับบรรทัดของใบสั่งขาย',
        text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${PRODUCT_MUST_MATCH_SALES_ORDER_LINE} — กล่องต้องแพ็กสินค้าของบรรทัดนั้นเท่านั้น สินค้าที่กรอกไปกับสินค้าที่บรรทัดต้องการอยู่ในรายละเอียดด้านล่าง`,
      };
    case PACKING_STANDARD_NOT_ACTIVE:
      return {
        heading: 'สินค้านี้ไม่มี Packing Standard ที่ใช้งานอยู่',
        text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${PACKING_STANDARD_NOT_ACTIVE} — ต้องตั้ง Packing Standard ให้สินค้านี้ก่อนจึงจะแพ็กได้`,
      };
    case STORAGE_LOCATION_NOT_FOUND:
      return {
        heading: 'ไม่พบที่เก็บรหัสนี้ในโรงงานนี้',
        text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${STORAGE_LOCATION_NOT_FOUND} — ตรวจรหัสที่เก็บอีกครั้ง (ที่เก็บผูกกับโรงงานเดียว)`,
      };
    case QUANTITY_MUST_BE_POSITIVE:
      return {
        heading: 'จำนวนที่กรอกต้องมากกว่าศูนย์',
        text: `เซิร์ฟเวอร์ตอบ 400 พร้อมกฎ ${QUANTITY_MUST_BE_POSITIVE} — จำนวนศูนย์หรือติดลบคือการไม่ใส่อะไรลงกล่อง ดูว่าฟิลด์ไหนที่เซิร์ฟเวอร์ชี้ในรายละเอียดด้านล่าง`,
      };
    case PACKING_UNIT_HAS_NO_LOTS:
      return {
        heading: 'กล่องนี้ยังไม่มี lot เลย',
        text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${PACKING_UNIT_HAS_NO_LOTS} — กล่องที่ไม่มี lot คือกล่องที่ตามสอบไม่ได้ ต้องมีอย่างน้อยหนึ่ง lot`,
      };
    case LOT_DUPLICATED_IN_PACKAGE:
      return {
        heading: 'lot เดียวกันถูกใส่ซ้ำในกล่องเดียว',
        text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${LOT_DUPLICATED_IN_PACKAGE} — ลบแถวที่ซ้ำออกหนึ่งแถว`,
      };
    case LOT_NOT_FOUND:
      return {
        heading: 'ไม่พบ lot รหัสนี้',
        text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${LOT_NOT_FOUND} — ไม่มี lot รหัสนี้ในทะเบียน ตรวจรหัส lot อีกครั้ง`,
      };
    case LOT_PLANT_MUST_MATCH_PACKAGE:
      return {
        heading: 'lot นี้เป็นของอีกโรงงานหนึ่ง',
        text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${LOT_PLANT_MUST_MATCH_PACKAGE} — กล่องแพ็กได้เฉพาะ lot ของโรงงานตัวเอง (D-004)`,
      };
    case LOT_PRODUCT_MUST_MATCH_PACKAGE:
      return {
        heading: 'lot นี้เป็นของสินค้าอื่น',
        text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${LOT_PRODUCT_MUST_MATCH_PACKAGE} — กล่องผสม lot ได้แต่ต้องเป็นสินค้าเดียวกันทุก lot`,
      };
    case LOT_NOT_RELEASED:
      return {
        heading: 'lot นี้ยังไม่ RELEASED — ส่งลูกค้าไม่ได้',
        text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${LOT_NOT_RELEASED} — lot ที่ถูกกักคุณภาพส่งให้ลูกค้าไม่ได้ (ตรวจทั้งตอนเปิดกล่องและตอนยืนยัน เพราะ lot ถูกกักระหว่างสองจุดนี้ได้) สถานะคุณภาพจริงอยู่ในรายละเอียดด้านล่าง`,
      };
    case INSUFFICIENT_STOCK_AT_SHIPPING_LOCATION:
      return {
        heading: 'ที่เก็บมี lot นี้ไม่พอสำหรับกล่องนี้',
        text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${INSUFFICIENT_STOCK_AT_SHIPPING_LOCATION} — จำนวนที่เหลือหักกล่องอื่นที่จับจองไปแล้วไม่พอ จำนวนคงเหลือกับส่วนที่ขาดอยู่ในรายละเอียดด้านล่าง`,
      };
    case PACKAGE_NO_TAKEN:
      return {
        heading: 'เลขกล่องชนกับอีกกล่องที่เปิดพร้อมกัน',
        text: `เซิร์ฟเวอร์ตอบ 409 พร้อมกฎ ${PACKAGE_NO_TAKEN} — เลขกล่องเป็นของเซิร์ฟเวอร์ ไม่ใช่สิ่งที่กรอก ลองกดเปิดกล่องอีกครั้งได้ทันที`,
      };
    case PACKED_QUANTITY_MUST_MATCH_LOT_TOTAL:
      return {
        heading: 'ผลรวม lot ไม่เท่ากับจำนวนของกล่อง',
        text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${PACKED_QUANTITY_MUST_MATCH_LOT_TOTAL} — **นี่คือกฎที่ไม่มี constraint ฐานข้อมูลใดถืออยู่** (D-105 ข้อ 1) ตัวเลขทั้งสองข้างและผลต่างที่แท้จริงอยู่ในรายละเอียดด้านล่าง แก้จำนวนแล้วเปิดกล่องใหม่อีกครั้ง — กล่องนี้ยังไม่ถูกปิด`,
      };
    case PACKING_UNIT_TRANSITION_NOT_ALLOWED:
      return {
        heading: 'กล่องนี้อยู่ในสถานะที่กดสิ่งนี้ไม่ได้',
        text: `เซิร์ฟเวอร์ตอบ 409 พร้อมกฎ ${PACKING_UNIT_TRANSITION_NOT_ALLOWED} — สถานะปัจจุบันจริงและสถานะปลายทางอยู่ในรายละเอียดด้านล่าง โหลดรายการใหม่เพื่อดูสถานะจริงก่อนลองอีกครั้ง`,
      };
    case PACKING_UNIT_STATUS_CHANGED:
      return {
        heading: 'กล่องนี้เปลี่ยนไปตั้งแต่ตอนที่อ่านมา',
        text: `เซิร์ฟเวอร์ตอบ 409 พร้อมกฎ ${PACKING_UNIT_STATUS_CHANGED} — โหลดรายการใหม่เพื่อดูสถานะจริง`,
      };
    case RETURNED_PACKAGE_CANNOT_BE_UNPACKED:
      return {
        heading: 'กล่องนี้ถูกลูกค้าตีกลับมา — เปิดใหม่ไม่ได้ในเฟสนี้',
        text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${RETURNED_PACKAGE_CANNOT_BE_UNPACKED} — ของที่ออกไปหาลูกค้าแล้วกลับมามีคำถามด้านคุณภาพที่ยังไม่มีคำตอบ ระบบจึงยังไม่ให้เปิดกล่องนี้ใหม่ เหตุผลเต็มอยู่ในรายละเอียดด้านล่าง`,
      };
    case PACKING_UNIT_CANNOT_BE_UNPACKED:
      return {
        heading: 'กล่องนี้อยู่ในสถานะที่เปิดใหม่ไม่ได้',
        text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${PACKING_UNIT_CANNOT_BE_UNPACKED} — เปิดใหม่ได้เฉพาะกล่องที่ปิดแล้ว (PACKED) เท่านั้น สถานะจริงอยู่ในรายละเอียดด้านล่าง`,
      };
    case PACKING_UNIT_FIELD_IS_SERVER_OWNED:
      return {
        heading: 'คำขอนี้ส่งฟิลด์ที่เซิร์ฟเวอร์เป็นเจ้าของ',
        text: `เซิร์ฟเวอร์ตอบ 400 พร้อมกฎ ${PACKING_UNIT_FIELD_IS_SERVER_OWNED} — นี่ไม่ควรเกิดจากหน้าจอนี้ แจ้งผู้ดูแลระบบ`,
      };
    default:
      return null;
  }
}

function conflictCopy(currentStatus: string | null): FailureCopy {
  return {
    heading:
      currentStatus === null
        ? 'สถานะปัจจุบันของกล่องนี้ไม่รับสิ่งที่กด'
        : `กล่องนี้อยู่ที่ ${packingUnitStatusLabel(currentStatus)} แล้ว`,
    text: 'เซิร์ฟเวอร์ตอบ 409 — โหลดรายการใหม่เพื่อดูสถานะจริงก่อนลองอีกครั้ง การกดซ้ำจะได้คำตอบเดิม',
  };
}

function failureCopy(
  failure: PackingUnitFailure,
  context: PackingUnitFailureContext,
): FailureCopy {
  const writing = isWriteContext(context);
  const nothingChanged = nothingChangedText(context);

  switch (failure.status) {
    case 'unauthenticated':
      return {
        heading: 'ต้องเข้าสู่ระบบก่อนจึงจะใช้หน้าจอนี้ได้',
        text: 'เซิร์ฟเวอร์ตอบ 401 — เซสชันหมดอายุหรือถูกยกเลิกไปแล้ว (D-049)',
      };
    case 'forbidden':
      return (
        ruleCopy(failure.error.rule) ?? {
          heading: 'สิทธิ์ชุดนี้ไม่ครอบคลุมคำขอนี้',
          text: `เซิร์ฟเวอร์ตอบ 403 — คำขอนี้ต้องการโมดูล ${PACKING_MODULE} ในระดับที่สูงกว่าที่แผนกของคุณถืออยู่${nothingChanged}`,
        }
      );
    case 'not-found':
      return {
        heading: 'ไม่พบกล่องที่คำขอนี้อ้างถึง',
        text: `เซิร์ฟเวอร์ตอบ 404 — กล่องนี้ไม่มีอยู่แล้ว หรืออยู่ในโรงงานนอกขอบเขตของคุณ (สองกรณีนี้ตอบเหมือนกันโดยตั้งใจ) โหลดรายการใหม่แล้วลองอีกครั้ง${nothingChanged}`,
      };
    case 'conflict':
      return ruleCopy(failure.error.rule) ?? conflictCopy(currentStatusOf(failure));
    case 'rule-violation':
      return (
        ruleCopy(failure.error.rule) ?? {
          heading: 'คำขอนี้ติดกฎทางธุรกิจ',
          text: `เซิร์ฟเวอร์ตอบ 422 พร้อมรหัสกฎที่ชน — ไม่ใช่ข้อผิดพลาดของระบบ${nothingChanged}`,
        }
      );
    case 'bad-request':
      return (
        ruleCopy(failure.error.rule) ?? {
          heading: 'คำขอนี้ถูกปฏิเสธว่าไม่ถูกต้อง',
          text: `เซิร์ฟเวอร์ตอบ 400 — ข้อมูลที่ส่งไปไม่ตรงรูปแบบที่ API รับ${nothingChanged}`,
        }
      );
    case 'server-error':
      return {
        heading: `เซิร์ฟเวอร์ตอบ ${failure.httpStatus}`,
        text: writing
          ? 'คำขอไปถึงเซิร์ฟเวอร์แล้วแต่ทำงานไม่สำเร็จ และไม่มีอะไรยืนยันว่าข้อมูลถูกเปลี่ยนหรือไม่ — โหลดรายการใหม่ก่อนลองอีกครั้ง'
          : 'คำขอไปถึงเซิร์ฟเวอร์แล้วแต่ทำงานไม่สำเร็จ',
      };
    case 'network-error':
      return {
        heading: 'ติดต่อเซิร์ฟเวอร์ไม่ได้',
        text: writing
          ? `${failure.detail} — ไม่มีอะไรยืนยันว่าคำขอไปถึงเซิร์ฟเวอร์หรือไม่ โหลดรายการใหม่ก่อนลองอีกครั้ง`
          : `${failure.detail} — หน้าจอนี้จึงไม่มีข้อมูลจะแสดง`,
      };
    case 'invalid-response':
      return {
        heading: 'ข้อมูลจากเซิร์ฟเวอร์ผิดรูปแบบ',
        text: 'หน้าจอปฏิเสธที่จะแสดงกล่องที่อ่านได้ไม่ครบ — lot ที่หายไปหนึ่งแถวคือของที่ delivery tag จะไม่พิมพ์',
      };
    case 'cancelled':
      return {
        heading: 'ยกเลิกการเรียกข้อมูลแล้ว',
        text: 'คำขอถูกยกเลิกก่อนได้รับคำตอบ',
      };
  }
}

function envelopeOf(failure: PackingUnitFailure): ApiErrorEnvelope | null {
  return 'error' in failure ? failure.error : null;
}

function currentStatusOf(failure: PackingUnitFailure): string | null {
  const envelope = envelopeOf(failure);
  if (envelope === null) {
    return null;
  }
  const value = envelope.details.current_status;
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

/** ค่าใน `details` ที่ไม่ใช่ `rule` — เป็นเลขจริงจากเซิร์ฟเวอร์ (lot, ผลต่าง,
 * จำนวนคงเหลือ ฯลฯ) แสดงดิบตามที่ได้รับมา ไม่แปลความหมายซ้ำ */
function detailEntries(envelope: ApiErrorEnvelope): readonly (readonly [string, string])[] {
  return Object.entries(envelope.details)
    .filter(([key]) => key !== 'rule')
    .map(([key, value]) => [key, formatDetailValue(value)] as const);
}

function formatDetailValue(value: unknown): string {
  if (Array.isArray(value)) {
    return value.map((entry) => formatDetailValue(entry)).join(', ');
  }
  if (value === null || value === undefined) {
    return '—';
  }
  if (typeof value === 'object') {
    return JSON.stringify(value);
  }
  return String(value);
}

export interface PackingUnitFailureNoticeProps {
  readonly failure: PackingUnitFailure;
  readonly context: PackingUnitFailureContext;
  readonly onRetry?: () => void;
}

export function PackingUnitFailureNotice({
  failure,
  context,
  onRetry,
}: PackingUnitFailureNoticeProps) {
  const copy = failureCopy(failure, context);
  const envelope = envelopeOf(failure);
  const rule = ruleFromFailure(failure);
  const serverMessage = envelope === null ? null : envelope.message;
  const entries = envelope === null ? [] : detailEntries(envelope);
  const refusedByRule = failure.status === 'rule-violation' || failure.status === 'conflict';

  return (
    <div
      className="cap-notice"
      data-testid="pu-failure"
      data-failure={failure.status}
      data-context={context}
      data-rule={rule ?? ''}
      role="status"
    >
      <StatusBadge tone={refusedByRule ? 'warning' : 'danger'}>
        {refusedByRule ? 'ติดกฎทางธุรกิจ' : 'ทำรายการไม่สำเร็จ'}
      </StatusBadge>
      <h3 className="cap-notice__title">{copy.heading}</h3>
      <p className="cap-notice__text">{copy.text}</p>

      {rule === null ? null : (
        <p className="cap-notice__text">
          กฎที่ชน: <code className="cap-table__code" data-testid="pu-rule">{rule}</code>
        </p>
      )}

      {serverMessage === null ? null : (
        <p className="cap-notice__note" data-testid="pu-failure-message">
          ข้อความจากเซิร์ฟเวอร์: {serverMessage}
        </p>
      )}

      {entries.length === 0 ? null : (
        <dl className="cap-notice__details" data-testid="pu-failure-details">
          {entries.map(([key, value]) => (
            <div key={key} data-testid={`pu-failure-detail-${key}`}>
              <dt>{key}</dt>
              <dd>{value}</dd>
            </div>
          ))}
        </dl>
      )}

      {onRetry === undefined ? null : (
        <div className="cap-notice__actions">
          <button type="button" className="cap-button cap-button--ghost" onClick={onRetry}>
            ลองอีกครั้ง
          </button>
        </div>
      )}
    </div>
  );
}

/* --------------------------------------------------------------- หน้าจอ -- */

export interface PackingScreenProps {
  readonly permissions: PermissionsState;
  readonly apiBaseUrl: string;
  readonly fetchImpl?: typeof fetch;
}

type ListState =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly items: readonly PackingUnitSummary[] }
  | PackingUnitFailure;

type Pending =
  | { readonly kind: 'create' }
  | { readonly kind: PackingUnitTransition; readonly packingUnitId: string };

interface ActionOutcome {
  readonly packingUnitId: string;
  readonly transition: PackingUnitTransition;
  readonly failure: PackingUnitFailure | null;
  readonly packingUnit: PackingUnit | null;
}

interface LotRow {
  readonly key: number;
  readonly draft: PackingUnitLotDraft;
}

function newLotRow(key: number): LotRow {
  return { key, draft: EMPTY_PACKING_UNIT_LOT_DRAFT };
}

export function PackingScreen({ permissions, apiBaseUrl, fetchImpl }: PackingScreenProps) {
  const canCreate = can(permissions, PACKING_MODULE, 'CREATE');
  const canEdit = can(permissions, PACKING_MODULE, 'EDIT');
  const canWriteAnything = canCreate || canEdit;

  const [items, setItems] = useState<ListState>({ status: 'loading' });
  const [reloadToken, setReloadToken] = useState(0);
  const [pending, setPending] = useState<Pending | null>(null);

  const [soNumberInput, setSoNumberInput] = useState('');
  const [soNumberFilter, setSoNumberFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const [createOpen, setCreateOpen] = useState(false);
  const [createFields, setCreateFields] = useState<CreatePackingUnitFields>(
    EMPTY_CREATE_PACKING_UNIT_FIELDS,
  );
  const [createLots, setCreateLots] = useState<readonly LotRow[]>([newLotRow(0)]);
  const [nextLotKey, setNextLotKey] = useState(1);
  const [createFailure, setCreateFailure] = useState<PackingUnitFailure | null>(null);
  const [createdPackingUnit, setCreatedPackingUnit] = useState<PackingUnit | null>(null);

  const [actionOutcome, setActionOutcome] = useState<ActionOutcome | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;

    setItems((previous) => (previous.status === 'ready' ? previous : { status: 'loading' }));

    const options = { apiBaseUrl, fetchImpl, signal: controller.signal };
    const status = toPackingUnitStatusFilter(statusFilter);
    const soNumber = toPackingUnitSoNumberFilter(soNumberFilter);

    void fetchPackingUnits(
      {
        ...(status === undefined ? {} : { status }),
        ...(soNumber === undefined ? {} : { so_number: soNumber }),
      },
      options,
    ).then((result) => {
      if (!active || result.status === 'cancelled') {
        return;
      }
      setItems(result.status === 'ready' ? { status: 'ready', items: result.page.items } : result);
    });

    return () => {
      active = false;
      controller.abort();
    };
  }, [apiBaseUrl, fetchImpl, reloadToken, statusFilter, soNumberFilter]);

  const openCreate = () => {
    if (!canCreate) {
      return;
    }
    setCreateFields(EMPTY_CREATE_PACKING_UNIT_FIELDS);
    setCreateLots([newLotRow(nextLotKey)]);
    setNextLotKey((key) => key + 1);
    setCreateFailure(null);
    setCreatedPackingUnit(null);
    setCreateOpen(true);
  };

  const closeCreate = useCallback(() => {
    setCreateOpen(false);
  }, []);

  const updateField = (field: keyof CreatePackingUnitFields, value: string) => {
    setCreateFields((previous) => ({ ...previous, [field]: value }));
  };

  const addLot = () => {
    setCreateLots((previous) => [...previous, newLotRow(nextLotKey)]);
    setNextLotKey((key) => key + 1);
  };

  const removeLot = (key: number) => {
    setCreateLots((previous) => previous.filter((row) => row.key !== key));
  };

  const updateLot = (key: number, field: keyof PackingUnitLotDraft, value: string) => {
    setCreateLots((previous) =>
      previous.map((row) =>
        row.key === key ? { ...row, draft: { ...row.draft, [field]: value } } : row,
      ),
    );
  };

  const lotDrafts: readonly PackingUnitLotDraft[] = createLots.map((row) => row.draft);
  const createBody = toCreatePackingUnitBody(createFields, lotDrafts);
  const busy = pending !== null;

  /** ผลรวม lot เทียบกับจำนวนกล่อง — คำนวณสดทุกครั้งที่ fields/lots เปลี่ยน
   * (invariant ข้อ "THE SUM RULE IS THE SCREEN'S WHOLE JOB") */
  const sumSummary = packingSumSummary(createFields.packedQty, lotDrafts);

  const submitCreate = async () => {
    if (createBody === null || pending !== null || !canCreate) {
      return;
    }

    setPending({ kind: 'create' });
    setCreateFailure(null);
    setCreatedPackingUnit(null);

    const result = await createPackingUnit(createBody, { apiBaseUrl, fetchImpl });
    setPending(null);
    if (result.status === 'cancelled') {
      return;
    }
    if (result.status !== 'ready') {
      // **ไม่แตะรายการเลย และไม่ปิดกล่อง** — สิ่งที่กรอกไว้ยังอยู่ครบ ให้แก้แล้ว
      // ส่งใหม่ได้ทันที (การเปิดกล่องเป็น all-or-nothing ฝั่งเซิร์ฟเวอร์อยู่แล้ว)
      setCreateFailure(result);
      return;
    }

    setCreatedPackingUnit(result.packingUnit);
    setCreateOpen(false);
    setReloadToken((token) => token + 1);
  };

  const runTransition = async (
    item: PackingUnitSummary,
    transition: PackingUnitTransition,
  ) => {
    if (
      pending !== null ||
      !can(permissions, PACKING_MODULE, packingUnitActionPermission(transition))
    ) {
      return;
    }

    setPending({ kind: transition, packingUnitId: item.packing_unit_id });
    setActionOutcome(null);

    const options = { apiBaseUrl, fetchImpl };
    const result =
      transition === 'confirm'
        ? await confirmPackingUnit(item.packing_unit_id, options)
        : await unpackPackingUnit(item.packing_unit_id, options);

    setPending(null);
    if (result.status === 'cancelled') {
      return;
    }
    if (result.status !== 'ready') {
      setActionOutcome({
        packingUnitId: item.packing_unit_id,
        transition,
        failure: result,
        packingUnit: null,
      });
      return;
    }

    setActionOutcome({
      packingUnitId: item.packing_unit_id,
      transition,
      failure: null,
      packingUnit: result.packingUnit,
    });
    setReloadToken((token) => token + 1);
  };

  const listItems = items.status === 'ready' ? items.items : [];
  const anyRowHasActions = listItems.some(
    (item) => packingUnitLifecycleActionsFor(item.status).length > 0,
  );

  return (
    <div className="cap-page" data-testid="packing-units">
      <div className="cap-page__head">
        <div>
          <h1 className="cap-page__title">
            แพ็กสินค้า (Packing)
            <InfoHint
              label="เกี่ยวกับหน้าแพ็กสินค้า"
              text={
                <>
                  เปิดกล่องต่อบรรทัดใบสั่งขาย ใส่ lot ยืนยันปิดกล่อง — แก้กล่องที่ปิดแล้ว
                  ต้อง <strong>เปิดกล่องใหม่</strong> เท่านั้น ไม่มีเส้นทางแก้จำนวนตรง ๆ
                  (D-105)
                </>
              }
            />
          </h1>
        </div>
      </div>

      {!canWriteAnything ? (
        <div className="cap-notice" data-testid="pu-read-only" role="status">
          <StatusBadge tone="warning">ดูได้อย่างเดียว</StatusBadge>
          <h2 className="cap-notice__title">สิทธิ์ชุดนี้ยังเปิด/ขับกล่องไม่ได้</h2>
          <p className="cap-notice__text">
            สิทธิ์ที่เซิร์ฟเวอร์ส่งมาไม่มี flag <code className="cap-table__code">can_create</code>{' '}
            หรือ <code className="cap-table__code">can_edit</code> ของโมดูล{' '}
            <code className="cap-table__code">{PACKING_MODULE}</code> — รายการและตัวกรองยังใช้
            ได้ตามปกติ
          </p>
        </div>
      ) : null}

      {/* --------------------------------------------------- ตัวกรอง -- */}

      <form
        className="cap-filters"
        data-testid="pu-filter-form"
        onSubmit={(event) => {
          event.preventDefault();
          setSoNumberFilter(soNumberInput);
        }}
      >
        <label className="cap-field">
          <span className="cap-field__label">
            เลขใบสั่งขาย
            <InfoHint
              label="เกี่ยวกับตัวกรองเลขใบสั่งขาย"
              text={
                <>
                  ตัวกรองจริงของเซิร์ฟเวอร์ (<code className="cap-table__code">?so_number=</code>) —
                  กด &quot;กรอง&quot; เพื่อถามใหม่ ไม่ใช่กรองหน้าที่โหลดมาแล้ว
                </>
              }
            />
          </span>
          <input
            className="cap-input"
            type="text"
            name="pu_so_number_filter"
            data-testid="pu-filter-so-number"
            value={soNumberInput}
            maxLength={SO_NUMBER_MAX_LENGTH}
            autoComplete="off"
            onChange={(event) => {
              setSoNumberInput(event.target.value);
            }}
          />
        </label>

        <button type="submit" className="cap-button cap-button--ghost" data-testid="pu-filter-apply">
          กรอง
        </button>

        <label className="cap-field">
          <span className="cap-field__label">สถานะ</span>
          <select
            className="cap-input"
            name="pu_status_filter"
            data-testid="pu-filter-status"
            value={statusFilter}
            onChange={(event) => {
              setStatusFilter(event.target.value);
            }}
          >
            <option value="">ทุกสถานะ</option>
            {PACKING_UNIT_STATUS_DESCRIPTORS.map((descriptor) => (
              <option key={descriptor.code} value={descriptor.code}>
                {descriptor.label}
              </option>
            ))}
          </select>
        </label>

        <button
          type="button"
          className="cap-button cap-button--primary"
          data-testid="pu-create-open"
          disabled={!canCreate}
          title={canCreate ? undefined : `ต้องมีสิทธิ์ ${PACKING_MODULE} + CREATE จึงจะเปิดกล่องใหม่ได้`}
          onClick={openCreate}
        >
          เปิดกล่องใหม่
        </button>
        {canCreate ? null : (
          <span className="cap-field__hint" data-testid="pu-create-open-reason">
            ต้องมีสิทธิ์ {PACKING_MODULE} + CREATE จึงจะเปิดกล่องใหม่ได้
          </span>
        )}
      </form>

      {/* ----------------------------------------------------- สร้างใหม่ -- */}

      <Modal open={canCreate && createOpen} onClose={closeCreate} title="เปิดกล่องใหม่ (Packing)">
        <form
          className="cap-form"
          data-testid="pu-create-form"
          aria-label="เปิดกล่องใหม่ต่อบรรทัดใบสั่งขาย"
          onSubmit={(event) => {
            event.preventDefault();
            void submitCreate();
          }}
        >
          <label className="cap-field">
            <span className="cap-field__label">รหัสโรงงาน</span>
            <input
              className="cap-input"
              name="pu_plant_code"
              data-testid="pu-create-plant"
              value={createFields.plantCode}
              maxLength={PLANT_CODE_MAX_LENGTH}
              autoComplete="off"
              disabled={busy}
              onChange={(event) => {
                updateField('plantCode', event.target.value);
              }}
            />
          </label>

          <label className="cap-field">
            <span className="cap-field__label">
              เลขใบสั่งขาย
              <InfoHint
                label="เกี่ยวกับเลขใบสั่งขายที่ใช้เปิดกล่อง"
                text="ต้องเป็นใบที่ยืนยัน/วางแผน/ส่งบางส่วนแล้ว (ไม่ใช่ DRAFT หรือ CANCELLED)"
              />
            </span>
            <input
              className="cap-input"
              name="pu_so_number"
              data-testid="pu-create-so-number"
              value={createFields.soNumber}
              maxLength={SO_NUMBER_MAX_LENGTH}
              autoComplete="off"
              disabled={busy}
              onChange={(event) => {
                updateField('soNumber', event.target.value);
              }}
            />
          </label>

          <label className="cap-field">
            <span className="cap-field__label">เลขบรรทัด</span>
            <input
              className="cap-input"
              name="pu_line_no"
              data-testid="pu-create-line-no"
              inputMode="numeric"
              value={createFields.lineNo}
              autoComplete="off"
              disabled={busy}
              onChange={(event) => {
                updateField('lineNo', event.target.value);
              }}
            />
          </label>

          <label className="cap-field">
            <span className="cap-field__label">
              รหัสสินค้า
              <InfoHint label="เกี่ยวกับรหัสสินค้า" text="ต้องตรงกับสินค้าของบรรทัดที่เลือก" />
            </span>
            <input
              className="cap-input"
              name="pu_product_code"
              data-testid="pu-create-product"
              value={createFields.productCode}
              maxLength={PRODUCT_CODE_MAX_LENGTH}
              autoComplete="off"
              disabled={busy}
              onChange={(event) => {
                updateField('productCode', event.target.value);
              }}
            />
          </label>

          <label className="cap-field">
            <span className="cap-field__label">รหัสที่เก็บ (ต้นทาง)</span>
            <input
              className="cap-input"
              name="pu_storage_location_code"
              data-testid="pu-create-location"
              value={createFields.storageLocationCode}
              maxLength={STORAGE_LOCATION_CODE_MAX_LENGTH}
              autoComplete="off"
              disabled={busy}
              onChange={(event) => {
                updateField('storageLocationCode', event.target.value);
              }}
            />
          </label>

          <label className="cap-field">
            <span className="cap-field__label">จำนวนของกล่อง</span>
            <input
              className="cap-input"
              name="pu_packed_qty"
              data-testid="pu-create-packed-qty"
              inputMode="decimal"
              value={createFields.packedQty}
              autoComplete="off"
              disabled={busy}
              onChange={(event) => {
                updateField('packedQty', event.target.value);
              }}
            />
          </label>

          {/* --------------------------------------------------- lot -- */}

          <fieldset className="cap-field" data-testid="pu-create-lots">
            <legend className="cap-field__label">
              Lot ที่จะใส่ในกล่อง (ผสม lot ได้)
              <InfoHint
                label="เกี่ยวกับการกรอก lot"
                text={
                  <>
                    lot ทั้งหมดของกล่องต้องกรอกที่นี่ — ไม่มีปุ่ม &quot;เพิ่ม lot&quot; แยกสำหรับกล่องที่
                    เปิดไว้แล้ว (backend ไม่มี route นั้น) ต้องมีอย่างน้อยหนึ่งแถว
                  </>
                }
              />
            </legend>

            <div className="cap-table-wrap">
              <table className="cap-table" data-testid="pu-create-lots-table">
                <caption className="cap-visually-hidden">
                  Lot ที่จะใส่ลงกล่องนี้ พร้อมปุ่มลบรายแถว
                </caption>
                <thead>
                  <tr>
                    <th scope="col">รหัส Lot</th>
                    <th scope="col">จำนวนของ lot นี้</th>
                    <th scope="col">ลบ</th>
                  </tr>
                </thead>
                <tbody>
                  {createLots.map((row, index) => (
                    <tr key={row.key} data-testid={`pu-create-lot-${index + 1}`}>
                      <td>
                        <input
                          className="cap-input"
                          name={`lot_id_${index + 1}`}
                          data-testid={`pu-create-lot-id-${index + 1}`}
                          aria-label={`รหัส lot แถวที่ ${index + 1}`}
                          value={row.draft.lotId}
                          autoComplete="off"
                          disabled={busy}
                          onChange={(event) => {
                            updateLot(row.key, 'lotId', event.target.value);
                          }}
                        />
                      </td>
                      <td>
                        <input
                          className="cap-input"
                          name={`lot_packed_qty_${index + 1}`}
                          data-testid={`pu-create-lot-qty-${index + 1}`}
                          aria-label={`จำนวน lot แถวที่ ${index + 1}`}
                          value={row.draft.packedQty}
                          inputMode="decimal"
                          autoComplete="off"
                          disabled={busy}
                          onChange={(event) => {
                            updateLot(row.key, 'packedQty', event.target.value);
                          }}
                        />
                      </td>
                      <td>
                        <button
                          type="button"
                          className="cap-button cap-button--ghost"
                          data-testid={`pu-create-lot-remove-${index + 1}`}
                          aria-label={`ลบแถวที่ ${index + 1}`}
                          disabled={busy}
                          onClick={() => {
                            removeLot(row.key);
                          }}
                        >
                          ลบแถว
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <button
              type="button"
              className="cap-button cap-button--ghost"
              data-testid="pu-create-lot-add"
              disabled={busy}
              onClick={addLot}
            >
              เพิ่มแถว lot
            </button>
          </fieldset>

          {/**
           * THE SUM HEADLINE — ผลรวม lot เทียบกับจำนวนกล่อง แสดงสดขณะกรอก
           * (invariant "THE SUM RULE IS THE SCREEN'S WHOLE JOB") ปุ่มยืนยันด้านล่าง
           * **ไม่ถูกปิดเมื่อไม่ตรง** — กฎนี้ต้องเดินทางไปให้เซิร์ฟเวอร์ปฏิเสธจริงเสมอ
           */}
          <div
            className="cap-notice"
            data-testid="pu-create-sum"
            data-matches={sumSummary.matches}
            role="status"
          >
            <p className="cap-notice__text">
              รวม lot: <strong data-testid="pu-create-lot-total">{sumSummary.lotTotal}</strong> /
              จำนวนกล่อง:{' '}
              <strong data-testid="pu-create-packed-qty-echo">
                {createFields.packedQty.trim() === '' ? '—' : createFields.packedQty.trim()}
              </strong>{' '}
              / ผลต่าง (กล่อง − lot):{' '}
              <strong data-testid="pu-create-sum-difference">
                {sumSummary.difference ?? '—'}
              </strong>
              <InfoHint
                label="เกี่ยวกับผลต่างนี้"
                text={
                  <>
                    ผลต่างนี้เป็นแค่ตัวช่วยของหน้าจอ — เซิร์ฟเวอร์เป็นผู้บังคับกฎนี้จริงตอนกด
                    &quot;ยืนยันปิดกล่อง&quot; (ไม่มี constraint ฐานข้อมูลถือกฎนี้อยู่, D-105 ข้อ 1)
                  </>
                }
              />
            </p>
          </div>

          <div className="cap-form__actions">
            <button
              type="submit"
              className="cap-button cap-button--primary"
              data-testid="pu-create-submit"
              disabled={busy || createBody === null}
            >
              {pending?.kind === 'create' ? 'กำลังเปิดกล่อง…' : 'เปิดกล่อง'}
            </button>
            <button
              type="button"
              className="cap-button cap-button--ghost"
              data-testid="pu-create-cancel"
              disabled={busy}
              onClick={closeCreate}
            >
              ยกเลิก
            </button>
          </div>

          {createFailure === null ? null : (
            <PackingUnitFailureNotice failure={createFailure} context="create" />
          )}
        </form>
      </Modal>

      {createdPackingUnit === null ? null : (
        <div className="cap-notice" data-testid="pu-create-result" role="status">
          <StatusBadge tone="success">เปิดกล่องแล้ว</StatusBadge>
          <h2 className="cap-notice__title">
            เปิดกล่อง {createdPackingUnit.package_no} แล้ว
          </h2>
          <p className="cap-notice__text">
            สถานะ {packingUnitStatusLabel(createdPackingUnit.status)} — มี{' '}
            {createdPackingUnit.lots.length} lot จำนวนรวม {createdPackingUnit.packed_qty}{' '}
            {createdPackingUnit.uom_code}
          </p>
        </div>
      )}

      {/* --------------------------------------------- ผลของปุ่มที่กดสำเร็จ -- */}

      {actionOutcome?.packingUnit == null ? null : (
        <div className="cap-notice" data-testid="pu-action-result" role="status">
          <StatusBadge tone="success">บันทึกแล้ว</StatusBadge>
          <h2 className="cap-notice__title">
            กล่อง {actionOutcome.packingUnit.package_no} อยู่ที่สถานะ{' '}
            {packingUnitStatusLabel(actionOutcome.packingUnit.status)} แล้ว
          </h2>
        </div>
      )}

      {/* ------------------------------------------------------- รายการ -- */}

      {items.status === 'loading' ? (
        <div className="cap-notice" data-testid="pu-loading" role="status">
          <p className="cap-notice__text">กำลังโหลดรายการกล่อง…</p>
        </div>
      ) : null}

      {items.status !== 'loading' && items.status !== 'ready' ? (
        <PackingUnitFailureNotice
          failure={items}
          context="list"
          onRetry={() => {
            setReloadToken((token) => token + 1);
          }}
        />
      ) : null}

      {items.status === 'ready' && listItems.length === 0 ? (
        <div className="cap-notice" data-testid="pu-empty" role="status">
          <StatusBadge tone="neutral">ยังไม่มีกล่อง</StatusBadge>
          <h2 className="cap-notice__title">
            {statusFilter === '' && soNumberFilter === ''
              ? 'ยังไม่มีกล่องในโรงงานที่คุณเข้าถึงได้'
              : 'ไม่มีกล่องที่ตรงกับตัวกรองนี้'}
          </h2>
        </div>
      ) : null}

      {items.status === 'ready' && listItems.length > 0 ? (
        <section className="cap-card cap-card--table" aria-label="กล่องทั้งหมด">
          <div className="cap-card__head">
            <h2 className="cap-card__title">กล่องทั้งหมด</h2>
          </div>
          <div className="cap-table-wrap">
            <table className="cap-table" data-testid="pu-table">
              <caption className="cap-visually-hidden">
                กล่องของโรงงานที่คุณเข้าถึงได้ พร้อมสถานะและปุ่มที่สถานะนั้นอนุญาต
              </caption>
              <thead>
                <tr>
                  <th scope="col">เลขกล่อง</th>
                  <th scope="col">โรงงาน</th>
                  <th scope="col">ใบสั่งขาย</th>
                  <th scope="col">บรรทัด</th>
                  <th scope="col">สินค้า</th>
                  <th scope="col">จำนวน</th>
                  <th scope="col">สถานะ</th>
                  {anyRowHasActions ? <th scope="col">การดำเนินการ</th> : null}
                </tr>
              </thead>
              <tbody>
                {listItems.map((item) => {
                  const actions = packingUnitLifecycleActionsFor(item.status);
                  const outcome =
                    actionOutcome?.packingUnitId === item.packing_unit_id &&
                    actionOutcome.failure !== null
                      ? actionOutcome
                      : null;

                  return (
                    <tr
                      key={item.packing_unit_id}
                      data-testid={`pu-row-${item.package_no}`}
                      data-status={item.status}
                    >
                      <th scope="row" className="cap-table__row-head">
                        <code className="cap-table__code">{item.package_no}</code>
                      </th>
                      <td>
                        <code className="cap-table__code">{item.plant_code}</code>
                      </td>
                      <td>
                        <code className="cap-table__code">{item.so_number}</code>
                      </td>
                      <td>{item.line_no}</td>
                      <td>
                        <code className="cap-table__code">{item.product_code}</code>
                      </td>
                      <td>
                        {item.packed_qty} {item.uom_code}
                      </td>
                      <td>
                        <StatusBadge tone={packingUnitStatusTone(item.status)}>
                          {packingUnitStatusLabel(item.status)}
                        </StatusBadge>
                      </td>
                      {anyRowHasActions ? (
                        <td>
                          {actions.map((transition) => {
                            const allowed = can(
                              permissions,
                              PACKING_MODULE,
                              packingUnitActionPermission(transition),
                            );
                            return (
                              <span key={transition}>
                                <button
                                  type="button"
                                  className="cap-button cap-button--ghost"
                                  data-testid={`pu-action-${transition}-${item.package_no}`}
                                  disabled={busy || !allowed}
                                  title={
                                    allowed
                                      ? undefined
                                      : `ต้องมีสิทธิ์ ${PACKING_MODULE} + ${packingUnitActionPermission(transition)}`
                                  }
                                  onClick={() => {
                                    void runTransition(item, transition);
                                  }}
                                >
                                  {pending?.kind === transition &&
                                  pending.packingUnitId === item.packing_unit_id
                                    ? 'กำลังส่ง…'
                                    : packingUnitActionLabel(transition)}
                                </button>
                                {allowed ? null : (
                                  <span
                                    className="cap-field__hint"
                                    data-testid={`pu-action-reason-${transition}-${item.package_no}`}
                                  >
                                    ต้องมีสิทธิ์ {PACKING_MODULE} +{' '}
                                    {packingUnitActionPermission(transition)}
                                  </span>
                                )}
                              </span>
                            );
                          })}

                          {outcome === null || outcome.failure === null ? null : (
                            <div data-testid="pu-action-failure-slot">
                              <PackingUnitFailureNotice
                                failure={outcome.failure}
                                context={outcome.transition}
                              />
                            </div>
                          )}
                        </td>
                      ) : null}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}
    </div>
  );
}
